/**
 * Builds the worksheet render-slot array for Report.tsx.
 *
 * Each slot is a {type, index, render?} tuple. `render` is undefined when
 * the worksheet does not provide the data for that panel — the consumer
 * filters those out. The TAGS slot is conditional on the worksheet having
 * a TAGS section (legacy templates never do).
 *
 * Closure-heavy by design: returning a render-function array (rather than a
 * <WorksheetPanels> subcomponent) keeps the ~30 captured deps local to the
 * parent and avoids a wide prop interface. See plan §2a.
 */

import React from 'react';
import {
  ContextPanel,
  MeasurementsPanel,
  FindingsPanel,
  FollowUpPanel,
  ConclusionsPanel,
  TagsPanel,
} from '@/features/report';
import type {
  RichTextEditorHandle,
  MeasurementRowModel,
} from '@/features/report';
import {
  Context,
  FindingStatement,
  FollowUp,
  Measurement,
} from '@/models/report';
import type { AIMeasurement } from '@/models/aiScan';
import {
  FollowUpGroup,
  MeasurementLabel,
  TemplateSection,
  Worksheet,
} from '@/models/template';
import { Study } from '@/models/study';

export interface WorksheetSlotDeps {
  reportWorksheet: Worksheet | undefined;
  orderedSections: TemplateSection[];
  tagsSection: TemplateSection | undefined;
  tagsCatalog: string[];

  // Report data + derived
  currentReport: {
    isFinalized?: boolean;
    aiMeasurements?: AIMeasurement[];
    measurements?: Measurement[];
  } | null;
  study: Study;
  /** Per-canonical resolved row state — built once in Report.tsx via
   *  `resolveMeasurementRows` and shared with the measurement click handlers. */
  rowModels: Map<string, MeasurementRowModel>;
  /** Engine recalc status (AP-632 R4): derived/indexed rows missing a required
   *  input, keyed by canonicalMeasurement → the missing input keys. */
  /** Derived/indexed rows the engine auto-updated but not yet saved (AP-632 R7)
   *  — drives the orange unsaved-change outline. */
  unsavedMeasurements: Set<string>;
  contextRecord: Record<string, Context>;

  // Local editor state
  findings: Record<string, FindingStatement[]>;
  conclusion: { value: string } | null;
  selectedFlags: Set<string>;
  followUp: FollowUp | null;
  reportTags: string[];

  // Derived from template + role
  disabledFlags: Set<string>;
  disabledFlagHints: Map<string, string>;
  followUpGroups: FollowUpGroup[];
  activeCanonicalMeasurement: string | null;
  selectedCanonicalMeasurement: string | null;
  /** Opens a guideline PDF from a measurement's range tooltip (useOpenGuideline). */
  onOpenGuideline?: (file: string, page?: number | null) => void;

  // Refs
  conclusionEditorRef: React.RefObject<RichTextEditorHandle | null>;

  // Handlers
  handleMeasurementClick: (label: MeasurementLabel) => void;
  handleMeasurementFocus: () => void;
  handleMeasurementValueChange: (
    canonicalMeasurement: string,
    value: number | null,
    unit: string | null
  ) => void;
  /** Reset a diverged computed row to its calculated value (AP-731 R17). */
  handleConverge: (canonicalMeasurement: string) => void;
  handleContextChange: (contextClass: string, value: string) => void;
  handleFindingChange: (
    findingClass: string,
    value: FindingStatement[]
  ) => void;
  handleConclusionChange: (value: string) => void;
  handleConclusionFocus: () => void;
  handleConclusionBlur: () => void;
  handleFlagToggle: (flagClass: string, checked: boolean) => void;
  handleFollowUpChange: (value: FollowUp | null) => void;
  handleTagsChange: (next: string[]) => void;

  // Role gating
  canEdit: boolean;
}

export interface WorksheetSlot {
  type: string;
  index: number;
  render?: () => React.ReactNode;
}

export function buildWorksheetSlots(deps: WorksheetSlotDeps): WorksheetSlot[] {
  const {
    reportWorksheet,
    orderedSections,
    tagsSection,
    tagsCatalog,
    currentReport,
    study,
    rowModels,
    unsavedMeasurements,
    contextRecord,
    findings,
    conclusion,
    selectedFlags,
    followUp,
    reportTags,
    disabledFlags,
    disabledFlagHints,
    followUpGroups,
    activeCanonicalMeasurement,
    selectedCanonicalMeasurement,
    conclusionEditorRef,
    handleMeasurementClick,
    handleMeasurementFocus,
    handleMeasurementValueChange,
    handleConverge,
    handleContextChange,
    handleFindingChange,
    handleConclusionChange,
    handleConclusionFocus,
    handleConclusionBlur,
    handleFlagToggle,
    handleFollowUpChange,
    handleTagsChange,
    canEdit,
    onOpenGuideline,
  } = deps;

  const indexFor = (type: string, fallback: number): number =>
    orderedSections.find((s) => s.sectionType === type)?.index ?? fallback;

  const disabled = !!currentReport?.isFinalized || !canEdit;

  const slots: WorksheetSlot[] = [
    {
      type: 'CONTEXT',
      index: indexFor('CONTEXT', 0),
      render: reportWorksheet?.contextGroups
        ? () => (
            <ContextPanel
              contextGroups={reportWorksheet.contextGroups}
              context={contextRecord}
              onContextChange={handleContextChange}
              rowModels={rowModels}
              unsavedMeasurements={unsavedMeasurements}
              onMeasurementChange={handleMeasurementValueChange}
              onConverge={handleConverge}
              disabled={disabled}
              patientUuid={study.patientUuid}
              currentStudyUuid={study.uuid}
            />
          )
        : undefined,
    },
    {
      type: 'MEASUREMENTS',
      index: indexFor('MEASUREMENTS', 1),
      render: reportWorksheet?.measurementGroups
        ? () => (
            <MeasurementsPanel
              measurementGroups={reportWorksheet.measurementGroups}
              rowModels={rowModels}
              unsavedMeasurements={unsavedMeasurements}
              onMeasurementChange={handleMeasurementValueChange}
              onConverge={handleConverge}
              onMeasurementClick={handleMeasurementClick}
              onMeasurementFocus={handleMeasurementFocus}
              activeMeasurement={activeCanonicalMeasurement}
              selectedMeasurement={selectedCanonicalMeasurement}
              disabled={disabled}
              onOpenSource={onOpenGuideline}
            />
          )
        : undefined,
    },
    {
      type: 'FINDINGS',
      index: indexFor('FINDINGS', 2),
      render:
        reportWorksheet?.findingGroups && reportWorksheet?.findingAttributes
          ? () => (
              <FindingsPanel
                findingGroups={reportWorksheet.findingGroups}
                findingAttributes={reportWorksheet.findingAttributes}
                findings={findings}
                onFindingChange={handleFindingChange}
                flagGroups={reportWorksheet.flagGroups}
                selectedFlags={selectedFlags}
                onFlagToggle={handleFlagToggle}
                disabledFlags={disabledFlags}
                disabledFlagHints={disabledFlagHints}
                disabled={disabled}
              />
            )
          : undefined,
    },
    {
      type: 'FOLLOW_UP',
      index: indexFor('FOLLOW_UP', 3),
      render:
        followUpGroups.length > 0
          ? () => (
              <FollowUpPanel
                followUpGroups={followUpGroups}
                value={followUp}
                onChange={handleFollowUpChange}
                disabled={disabled}
              />
            )
          : undefined,
    },
    {
      type: 'CONCLUSION',
      index: indexFor('CONCLUSION', 4),
      render: () => (
        <ConclusionsPanel
          ref={conclusionEditorRef}
          value={conclusion?.value ?? ''}
          onChange={handleConclusionChange}
          onFocus={handleConclusionFocus}
          onBlur={handleConclusionBlur}
          disabled={disabled}
        />
      ),
    },
  ];

  if (tagsSection) {
    slots.push({
      type: 'TAGS',
      index: tagsSection.index,
      render: () => (
        <TagsPanel
          catalog={tagsCatalog}
          value={reportTags}
          onChange={handleTagsChange}
          disabled={disabled}
        />
      ),
    });
  }

  return slots;
}
